/**
 * CachrContext.tsx - React wrapper for CachrManager
 * 
 * CHANGE HISTORY:
 * - 251125_2230 danielbeer: Add TypeScript types
 * - 251021_0543 [d4517d3] danielbeer: feat: apply performance optimizations to all packages
 * - 251019_1100 [b24c704] danielbeer: fix(cachr): remove incompatible react-hooks testing library
 * - 251019_1000 [0deebd6] danielbeer: refactor: Extract CacheManager to @opcentral/cachr package
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { getCachr } from './CachrManager';
import { useActr } from '@opcentral/actr';
import type { CachrOptions, CacheStats, SetResult } from './types';
import type { CachrManager } from './CachrManager';

interface CachrContextValue {
  cachr: CachrManager;
  stats: CacheStats;
  refreshStats: () => void;
  set: (id: string, data: any, metadata?: any) => Promise<SetResult>;
  get: (id: string, parseJson?: boolean) => any;
  remove: (id: string) => { success: boolean; error?: any };
  clearAll: () => { removed: number; stats: CacheStats };
  pruneCache: (targetBytes?: number | null) => { removed: number; freedSpace: number; formattedFreed: string; remainingItems: number; stats: CacheStats };
  has: (id: string) => boolean;
  getPreferences: () => any;
  savePreferences: (prefs: any) => void;
  forceSet: (id: string, data: any, metadata?: any) => Promise<SetResult>;
}

const CachrContext = createContext<CachrContextValue | null>(null);

interface CachrProviderProps {
  children: ReactNode;
  options?: CachrOptions;
}

/**
 * CachrProvider - Provides cache management to React components
 */
export const CachrProvider: React.FC<CachrProviderProps> = ({ children, options = {} }) => {
  const actr = useActr();
  const [cachr] = useState(() => getCachr({ ...options, actr }));
  const [stats, setStats] = useState(() => cachr.getStats());

  // Refresh stats when cache changes
  const refreshStats = useCallback(() => {
    setStats(cachr.getStats());
  }, [cachr]);

  // Convenience methods that also refresh stats
  const set = useCallback((id: string, data: any, metadata?: any) => {
    const result = cachr.set(id, data, metadata);
    refreshStats();
    return result;
  }, [cachr, refreshStats]);

  const get = useCallback((id: string, parseJson?: boolean) => {
    return cachr.get(id, parseJson);
  }, [cachr]);

  const remove = useCallback((id: string) => {
    const result = cachr.remove(id);
    refreshStats();
    return result;
  }, [cachr, refreshStats]);

  const clearAll = useCallback(() => {
    const result = cachr.clearAll();
    refreshStats();
    return result;
  }, [cachr, refreshStats]);

  const pruneCache = useCallback((targetBytes?: number | null) => {
    const result = cachr.pruneCache(targetBytes);
    refreshStats();
    return result;
  }, [cachr, refreshStats]);

  const has = useCallback((id: string) => {
    return cachr.has(id);
  }, [cachr]);

  const listItems = useCallback(() => {
    return cachr.listItems();
  }, [cachr]);

  const getPreferences = useCallback(() => {
    return cachr.getPreferences();
  }, [cachr]);

  const savePreferences = useCallback((prefs: any) => {
    cachr.savePreferences(prefs);
    refreshStats();
  }, [cachr, refreshStats]);

  const forceSet = useCallback((id: string, data: any, metadata?: any) => {
    const result = cachr.forceSet(id, data, metadata);
    refreshStats();
    return result;
  }, [cachr, refreshStats]);

  const value = useMemo(() => ({
    cachr,
    stats,
    refreshStats,
    set,
    get,
    remove,
    clearAll,
    pruneCache,
    has,
    listItems,
    getPreferences,
    savePreferences,
    forceSet
  }), [cachr, stats, refreshStats, set, get, remove, clearAll, pruneCache, has, listItems, getPreferences, savePreferences, forceSet]);

  return (
    <CachrContext.Provider value={value}>
      {children}
    </CachrContext.Provider>
  );
};

/**
 * useCachr - Hook to access cache management
 * 
 * @returns {Object} Cache management methods and stats
 */
export const useCachr = () => {
  const context = useContext(CachrContext);
  if (!context) {
    throw new Error('useCachr must be used within CachrProvider');
  }
  return context;
};

/**
 * useCacheItem - Hook to manage a single cached item
 * 
 * @param {string} id - Cache item ID
 * @param {*} defaultValue - Default value if not cached
 * @returns {[value, setValue, isLoading, error]}
 */
export const useCacheItem = (id, defaultValue = null) => {
  const { get, set, has } = useCachr();
  const [value, setValue] = useState(defaultValue);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      if (has(id)) {
        const cached = get(id);
        setValue(cached);
      }
      setIsLoading(false);
    } catch (err) {
      setError(err);
      setIsLoading(false);
    }
  }, [id, get, has]);

  const updateValue = useCallback((newValue) => {
    try {
      const result = set(id, newValue);
      if (result.success) {
        setValue(newValue);
        setError(null);
      } else {
        setError(new Error(result.reason));
      }
    } catch (err) {
      setError(err);
    }
  }, [id, set]);

  return [value, updateValue, isLoading, error];
};

/**
 * useCacheStats - Hook to access cache statistics
 * 
 * @returns {Object} Cache statistics
 */
export const useCacheStats = () => {
  const { stats, refreshStats } = useCachr();
  
  useEffect(() => {
    // Refresh stats on mount
    refreshStats();
  }, [refreshStats]);

  return stats;
};
